import { Link } from "react-router-dom";

import { partialFootnote, partialMark } from "../domain/foodView";
import { PARTIAL_KEYS, sumState, type IntakeTotals, type PartialKey } from "../domain/intake";
import { nutrientRows, type DailyTargets } from "../domain/nutrition";
import type { FoodEntry } from "../domain/types";
import { ProgressBar } from "./ProgressBar";

const fmt = (n: number, unit: "g" | "mg") =>
  unit === "mg" ? Math.round(n).toLocaleString() : (Math.round(n * 10) / 10).toLocaleString();

const isPartialKey = (k: string): k is PartialKey => (PARTIAL_KEYS as string[]).includes(k);

/**
 * The day's nutrients against what the day needs, one ProgressBar each.
 *
 * Goals fill towards their target; a ceiling (sodium, saturated fat) is drawn as one, so going
 * over it turns the bar danger *and* says "over" — colour is never the only carrier of state. A
 * target Kaya cannot state says so and links to where it can be set. (ADR-026)
 *
 * A sum over foods where some lack the value is a lower bound (SNIPPETS §31): its figure reads
 * "≥ 1,900", the bar fills only what is known, and the foods that lack it are named under the bar.
 * A sum no food carries is "–", not 0 (invariant 18). One line at the end says what "≥" means.
 */
export function NutrientBars({
  totals,
  targets,
  entries,
}: {
  totals: IntakeTotals;
  targets: DailyTargets;
  /** the entries `totals` was summed from, to name the foods a partial sum leaves out */
  entries: FoodEntry[];
}) {
  const rows = nutrientRows(totals, targets);
  const footnote = partialFootnote(entries);

  return (
    <div className="k-food-nbars">
      {rows.map((r) => {
        const state = isPartialKey(r.key) ? sumState(totals, r.key) : "complete";
        const value = state === "none" ? null : r.value;
        const shown = value === null ? "–" : fmt(value, r.unit);
        const ge =
          state === "partial" ? (
            <span className="k-pbar__ge" aria-hidden="true">
              ≥{" "}
            </span>
          ) : null;
        const mark = isPartialKey(r.key) ? partialMark(entries, r.key) : null;
        const atLeast = state === "partial" ? "at least " : "";
        if (r.target === null) {
          return (
            <div className="k-pbar" key={r.key}>
              <div className="k-pbar__head">
                <span className="k-pbar__label">{r.label}</span>
                <span className="k-pbar__detail">
                  {ge}
                  {shown} {r.unit} · target –{" "}
                  <Link to="/you/body">Needs body numbers</Link>
                </span>
              </div>
            </div>
          );
        }
        return (
          <ProgressBar
            key={r.key}
            value={value}
            max={r.target}
            ceiling={r.ceiling}
            label={r.label}
            detail={
              <>
                {ge}
                {shown} {r.ceiling ? "of max" : "of"} {fmt(r.target, r.unit)} {r.unit}
              </>
            }
            partial={mark}
            ariaLabel={
              `${r.label}: ${atLeast}${shown} ${r.ceiling ? "of a maximum of" : "of"} ${fmt(r.target, r.unit)} ${r.unit}` +
              (mark ? `; ${mark}` : "")
            }
          />
        );
      })}
      {footnote ? <p className="k-food-note">{footnote}</p> : null}
    </div>
  );
}
